import { ATMOSPHERE_PALETTE as P } from '../palette';
import { stoneBiome } from '../biomeArchetypes';
import { COIL_WORLD_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/coilWorldBackdrops';
import { JOURNEY_DEPTH_BACKDROPS, JOURNEY_DEPTH_HEIGHTS } from '../../../game/effects/atmosphere/silhouettes/journeyDepthBackdrops';

/** Coil Undercroft — the dark heart beneath the lighthouse. Near-black stone
 *  with a sickly-green tinge, sparse sickly-green + bone spores, faint cold
 *  green shafts, a low sickly-green floor pool that breathes like something
 *  alive, a restless sickly-green light bed, cold green will-o'-wisp flicker,
 *  pooled heavy sickly-green fog + pulsing bioluminescence corruption cells,
 *  near-black coil/columns parallax (the lighthouse's inner spiral), and a
 *  HEAVY black vignette + very dark black-green grade. Cold-green dread, NOT
 *  warm torch — the deliberate inverse of the stock dungeon. The dread anchor. */
export const COIL_UNDERCROFT = stoneBiome({
  id: 'coil_undercroft',
  sky: { from: 0x06090a, to: 0x12231a },
  motes: { colors: [0x6fae4e, P.bone, 0x4a7d3a], count: 10, sizeMin: 6, sizeMax: 14, driftPx: 16, periodMinMs: 10000, periodMaxMs: 20000, alphaMin: 0.10, alphaMax: 0.40 },
  rays: { color: 0x4a7d3a, count: 2, widthMin: 50, widthMax: 120, height: 460, tiltDeg: 8, periodMinMs: 12000, periodMaxMs: 22000, alphaMin: 0.04, alphaMax: 0.14 },
  glow: {
    blobs: [
      // Low sickly-green floor pool — the corruption, not a hearth.
      { color: 0x4a7d3a, atX: 0.5, atY: 0.92, size: 520, alpha: 0.10 },
      { color: 0x6fae4e, atX: 0.3, atY: 0.85, size: 300, alpha: 0.07 },
    ],
    breathe: { periodMs: 4800, min: 0.60, max: 1.40 },
  },
  lightField: { palette: [0x4a7d3a, 0x6fae4e, 0x2e4a2a], count: 9, sizeMin: 80, sizeMax: 200, alphaMin: 0.03, alphaMax: 0.12, driftRadius: 40, periodMinMs: 8000, periodMaxMs: 16000 },
  flicker: { color: 0x6fae4e, size: 200, baseAlpha: 0.10, flickerAmp: 0.07, hzMin: 3, hzMax: 7, anchors: [{ atX: 0.15, atY: 0.55 }, { atX: 0.85, atY: 0.55 }, { atX: 0.5, atY: 0.45 }, { atX: 0.35, atY: 0.75 }, { atX: 0.68, atY: 0.72 }] },
  extraLayers: [
    // Pooled heavy sickly-green fog — the sea-fog has settled here.
    { kind: 'fog', color: 0x2a4a2a, bands: 5, alphaMin: 0.08, alphaMax: 0.20, driftPx: 30, periodMinMs: 12000, periodMaxMs: 24000, yMin: 0.5, yMax: 0.95, sizeMin: 200, sizeMax: 450 },
    // Pulsing sickly-green bioluminescence — the "alive" wrongness clinging to stone.
    { kind: 'bioluminescence', color: 0x6fae4e, count: 6, sizeMin: 80, sizeMax: 200, alphaMin: 0.08, alphaMax: 0.55, bandYMin: 0.3, bandYMax: 0.85, driftPx: 20, periodMinMs: 4000, periodMaxMs: 9000 },
  ],
  parallax: [
    // Authored silhouettes (Coil art pack): the lighthouse's inner spiral —
    // 8 thin bowing columns far, 5 mid, 2 massive near-black walls near, with
    // corruption-green seam glow deepening toward the player (8→13→18%).
    // The far vault reaches the entry gallery with narrow columns and open air.
    // Existing middle/near corruption stays concentrated around the lower arena.
    { shape: 'columns', color: 0x283830, speed: 0.03, height: JOURNEY_DEPTH_HEIGHTS.coil, alpha: 0.40, detail: 0.9, sway: { ampPx: 6, periodMs: 36000 }, svg: JOURNEY_DEPTH_BACKDROPS.coil },
    { shape: 'columns', color: 0x0e1a0c, speed: 0.08, height: 560, alpha: 0.86, detail: 1.1, svg: COIL_WORLD_BACKDROPS.undercroft.mid },
    { shape: 'columns', color: 0x06090a, speed: 0.16, height: 500, alpha: 0.95, detail: 1.2, svg: COIL_WORLD_BACKDROPS.undercroft.near },
  ],
  closer: {
    // HEAVY black vignette + faint sickly-green wash. The walls close in.
    vignette: { color: 0x000000, edgeAlpha: 0.48, washColor: 0x2a4a2a, washAlpha: 0.05 },
    grade: { top: 0x12231a, bottom: 0x06090a, strength: 0.18, blend: 'normal' },
  },
});
